<!-- 자재 월마감 조회 (2026-07-15, 1단계 조회 전용) —
     자재과 월별결산보고서(엑셀) 대체. 마감 확정·잠금은 오픈 운영 후 2단계.
     근거: docs/closing-folder-analysis-2026-07-15.md -->
<template>
  <div class="mc-view">
    <div class="page-header">
      <h2>자재 월마감</h2>
      <el-button size="small" @click="helpVisible = true">도움말</el-button>
      <HelpPanel v-model="helpVisible" :content-path="'screens/material-closing'" />
      <span class="hint">조회 전용 (1단계) — 마감 확정·잠금은 운영 후 도입</span>
    </div>

    <el-row :gutter="10" class="mb-2" align="middle">
      <el-col :span="5">
        <el-date-picker
          v-model="month" type="month" format="YYYY-MM" value-format="YYYY-MM"
          placeholder="마감월" :clearable="false" @change="fetchReport"
          style="width: 100%"
        />
      </el-col>
      <el-col :span="5">
        <el-select v-model="companyFilter" placeholder="법인 (전체)" clearable @change="fetchReport">
          <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id" />
        </el-select>
      </el-col>
      <el-col :span="14" style="text-align:right">
        <el-tooltip placement="bottom" effect="light">
          <template #content>
            <div style="max-width:340px">{{ report.note }}</div>
          </template>
          <el-button link type="info" size="small" @click="mapVisible = true">분류 매핑 확인</el-button>
        </el-tooltip>
      </el-col>
    </el-row>

    <!-- 6분류 요약 카드 -->
    <div class="summary-row" v-loading="loading">
      <div v-for="g in report.groups || []" :key="g" class="sum-card">
        <div class="sum-label">{{ g }}</div>
        <div class="sum-value">{{ fmt(report.summary?.[g]) }}</div>
      </div>
      <div class="sum-card sum-card--total">
        <div class="sum-label">합계</div>
        <div class="sum-value">{{ fmt(report.summary?.total) }}</div>
      </div>
    </div>

    <el-tabs v-model="activeTab" class="mt-2">
      <el-tab-pane :label="`업체별 명세 (${(report.by_supplier || []).length})`" name="supplier">
        <el-table :data="report.by_supplier || []" size="small" border stripe
          show-summary :summary-method="supplierSummary">
          <el-table-column prop="supplier" label="거래처" min-width="160" fixed />
          <el-table-column v-for="g in report.groups || []" :key="g" :label="g" align="right" min-width="100">
            <template #default="{ row }">{{ fmt(row[g], true) }}</template>
          </el-table-column>
          <el-table-column label="합계" align="right" min-width="110">
            <template #default="{ row }"><b>{{ fmt(row.total) }}</b></template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane :label="`품목군별 (${(report.by_item_group || []).length})`" name="item">
        <div class="hint mb-2">발주(입고) 연결된 지결 상세 기준 — 자재 마스터의 품목군으로 집계</div>
        <el-table :data="report.by_item_group || []" size="small" border stripe style="max-width: 560px">
          <el-table-column prop="category" label="품목군" min-width="140" />
          <el-table-column label="수량" align="right" width="120">
            <template #default="{ row }">{{ Number(row.quantity).toLocaleString() }}</template>
          </el-table-column>
          <el-table-column label="금액" align="right" width="150">
            <template #default="{ row }">{{ fmt(row.total) }}</template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="연간 추이" name="trend">
        <el-table :data="report.trend || []" size="small" border stripe>
          <el-table-column prop="month" label="마감월" width="90" fixed />
          <el-table-column v-for="g in report.groups || []" :key="g" :label="g" align="right" min-width="100">
            <template #default="{ row }">{{ fmt(row[g], true) }}</template>
          </el-table-column>
          <el-table-column label="합계" align="right" min-width="110">
            <template #default="{ row }"><b>{{ fmt(row.total) }}</b></template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <!-- 비용구분 → 분류 매핑 현황 -->
    <el-dialog v-model="mapVisible" title="비용구분 → 마감 분류 매핑" width="440px">
      <div class="hint mb-2">
        발주(입고) 연결 지결은 항상 <b>부자재</b>로 분류됩니다.
        그 외는 아래 매핑을 따릅니다 — 실무와 다르면 알려주세요.
      </div>
      <el-table :data="report.category_map || []" size="small" border max-height="420">
        <el-table-column prop="category" label="비용구분" min-width="160" />
        <el-table-column prop="group" label="마감 분류" width="110" />
      </el-table>
    </el-dialog>
  </div>
</template>

<script setup>
import HelpPanel from '@/components/help/HelpPanel.vue'
const helpVisible = ref(false)
import { ref, onMounted } from 'vue'
import dayjs from 'dayjs'
import axiosInstance from '@/api/axios'

const month = ref(dayjs().format('YYYY-MM'))
const companyFilter = ref(null)
const companies = ref([])
const report = ref({})
const loading = ref(false)
const activeTab = ref('supplier')
const mapVisible = ref(false)

const fmt = (v, blankZero = false) => {
  const n = Number(v || 0)
  if (blankZero && n === 0) return ''
  return n.toLocaleString()
}

const fetchReport = async () => {
  loading.value = true
  try {
    const params = { month: month.value }
    if (companyFilter.value) params.company = companyFilter.value
    const res = await axiosInstance.get('/material-closing/', { params })
    report.value = res.data || {}
  } catch (err) {
    console.error('자재 월마감 조회 실패:', err)
  } finally {
    loading.value = false
  }
}

const fetchCompanies = async () => {
  try {
    const res = await axiosInstance.get('/expense-company/')
    companies.value = Array.isArray(res.data) ? res.data : (res.data.results || [])
  } catch (err) {
    console.error('법인 목록 조회 실패:', err)
  }
}

const supplierSummary = ({ columns }) => {
  const rows = report.value.by_supplier || []
  return columns.map((col, i) => {
    if (i === 0) return '합계'
    const key = col.label === '합계' ? 'total' : col.label
    const sum = rows.reduce((acc, r) => acc + Number(r[key] || 0), 0)
    return sum ? sum.toLocaleString() : ''
  })
}

onMounted(() => { fetchReport(); fetchCompanies() })
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.mb-2 { margin-bottom: 8px; }
.mt-2 { margin-top: 12px; }
.hint { font-size: 12px; color: #909399; }
.summary-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.sum-card {
  flex: 1;
  min-width: 120px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 10px 14px;
  background: #fff;
}
.sum-card--total { background: #f5f7fa; }
.sum-label { font-size: 12px; color: #909399; margin-bottom: 4px; }
.sum-value { font-size: 17px; font-weight: 700; color: #303133; }
</style>
